'use client';

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Filter, Plus, Eye, Edit } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface Job {
  id: number;
  title: string;
  department: string;
  author: string;
  createdDate: string;
  status: string;
  responsibilities: string[];
  tasks: string[];
  competencies: string[];
  purpose: string;
  scope: string;
  knowledge: string[];
  skills: string[];
  attitude: string[];
  qualifications: string[];
  education: string[];
  previousJobs: string[];
  nextJobs: string[];
}

export function JobAnalysis() {
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newJob, setNewJob] = useState({
    title: '',
    department: '',
    purpose: '',
    responsibilities: '',
    knowledge: '',
    skills: '',
  });

  const jobData: Job[] = [
    {
      id: 1,
      title: '프론트엔드 개발자',
      department: '개발부',
      author: '김개발',
      createdDate: '2024.01.10',
      status: '완료',
      responsibilities: ['웹 애플리케이션 UI/UX 개발', 'React/Vue.js 컴포넌트 개발', '브라우저 호환성 확보'],
      tasks: ['요구사항 분석 및 설계', '코딩 및 테스트', '배포 및 유지보수'],
      competencies: ['JavaScript/TypeScript', 'React/Vue.js', '웹 표준', 'Git'],
      purpose: '사용자 친화적인 웹 인터페이스를 개발하여 최적의 사용자 경험을 제공',
      scope: '프론트엔드 개발 전반, UI/UX 구현, 성능 최적화',
      knowledge: ['웹 개발 프레임워크', '브라우저 동작 원리', 'RESTful API'],
      skills: ['프로그래밍', '디버깅', '성능 최적화'],
      attitude: ['협업능력', '학습능력', '문제해결능력'],
      qualifications: ['컴퓨터공학 관련 학과', '프론트엔드 개발 경력 2년 이상'],
      education: ['JavaScript 심화과정', 'React 전문과정'],
      previousJobs: ['웹 퍼블리셔', '주니어 개발자'],
      nextJobs: ['시니어 프론트엔드 개발자', '풀스택 개발자']
    },
    {
      id: 2,
      title: '영업 매니저',
      department: '영업부',
      author: '박영업',
      createdDate: '2024.01.08',
      status: '완료',
      responsibilities: ['신규 고객 발굴', '매출 목표 달성', '고객 관계 관리'],
      tasks: ['시장 조사', '영업 전략 수립', '고객 미팅'],
      competencies: ['영업 전략', '고객 관리', '협상 능력', '프레젠테이션'],
      purpose: '신규 고객 확보 및 기존 고객과의 관계 강화를 통한 매출 증대',
      scope: '영업 활동 전반, 고객 관리, 매출 분석',
      knowledge: ['영업 프로세스', '시장 동향', '제품 지식'],
      skills: ['협상', '프레젠테이션', '관계 구축'],
      attitude: ['적극성', '끈기', '고객 지향성'],
      qualifications: ['경영학/마케팅 관련 학과', '영업 경력 3년 이상'],
      education: ['영업 전략 과정', '고객 관리 시스템 교육'],
      previousJobs: ['영업 사원', '텔레마케터'],
      nextJobs: ['영업 총괄', '사업부장']
    },
    {
      id: 3,
      title: '마케팅 스페셜리스트',
      department: '마케팅부',
      author: '이마케팅',
      createdDate: '2024.01.05',
      status: '검토중',
      responsibilities: ['마케팅 캠페인 기획', '브랜드 관리', '시장 분석'],
      tasks: ['캠페인 설계', 'ROI 분석', '콘텐츠 제작'],
      competencies: ['디지털 마케팅', '데이터 분석', '창의적 사고', '프로젝트 관리'],
      purpose: '브랜드 인지도 향상 및 고객 확보를 위한 마케팅 전략 수립 및 실행',
      scope: '마케팅 전략 수립, 캠페인 실행, 성과 분석',
      knowledge: ['마케팅 이론', '소비자 행동', '시장 조사'],
      skills: ['데이터 분석', '창의적 기획', '프로젝트 관리'],
      attitude: ['창의성', '분석적 사고', '트렌드 민감성'],
      qualifications: ['마케팅/광고 관련 학과', '디지털 마케팅 경력 2년 이상'],
      education: ['디지털 마케팅 과정', 'Google Analytics 인증'],
      previousJobs: ['마케팅 어시스턴트', '광고 기획자'],
      nextJobs: ['마케팅 매니저', '브랜드 총괄']
    }
  ];

  const filteredJobs = jobData.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.department.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = selectedDepartment === 'all' || job.department === selectedDepartment;
    return matchesSearch && matchesDepartment;
  });

  const departments = ['all', '개발부', '영업부', '마케팅부', '인사부', '재무부'];

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold">직무분석</h1>
          <p className="text-xs sm:text-sm text-gray-600">조직 내 모든 직무의 분석 및 관리</p>
        </div>
        <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="text-xs sm:text-sm">
              <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              새 직무 등록
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-base sm:text-lg">새 직무 등록</DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">
                새로운 직무를 등록합니다.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-3 sm:gap-4 py-2 sm:py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="title" className="text-xs sm:text-sm">직무명 *</Label>
                  <Input
                    id="title"
                    placeholder="예: 백엔드 개발자"
                    value={newJob.title}
                    onChange={(e) => setNewJob({ ...newJob, title: e.target.value })}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="department" className="text-xs sm:text-sm">부서 *</Label>
                  <Select
                    value={newJob.department}
                    onValueChange={(value) => setNewJob({ ...newJob, department: value })}
                  >
                    <SelectTrigger id="department" className="text-sm">
                      <SelectValue placeholder="부서 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="개발부">개발부</SelectItem>
                      <SelectItem value="영업부">영업부</SelectItem>
                      <SelectItem value="마케팅부">마케팅부</SelectItem>
                      <SelectItem value="인사부">인사부</SelectItem>
                      <SelectItem value="재무부">재무부</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1 sm:space-y-2">
                <Label htmlFor="purpose" className="text-xs sm:text-sm">직무 목적 *</Label>
                <Textarea
                  id="purpose"
                  placeholder="이 직무의 주요 목적을 입력하세요"
                  value={newJob.purpose}
                  onChange={(e) => setNewJob({ ...newJob, purpose: e.target.value })}
                  rows={2}
                  className="text-sm"
                />
              </div>

              <div className="space-y-1 sm:space-y-2">
                <Label htmlFor="responsibilities" className="text-xs sm:text-sm">주요 책무</Label>
                <Textarea
                  id="responsibilities"
                  placeholder="주요 책무를 줄바꿈으로 구분"
                  value={newJob.responsibilities}
                  onChange={(e) => setNewJob({ ...newJob, responsibilities: e.target.value })}
                  rows={2}
                  className="text-sm"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="knowledge" className="text-xs sm:text-sm">필요 지식</Label>
                  <Textarea
                    id="knowledge"
                    placeholder="필요한 지식"
                    value={newJob.knowledge}
                    onChange={(e) => setNewJob({ ...newJob, knowledge: e.target.value })}
                    rows={2}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-1 sm:space-y-2">
                  <Label htmlFor="skills" className="text-xs sm:text-sm">필요 기술</Label>
                  <Textarea
                    id="skills"
                    placeholder="필요한 기술"
                    value={newJob.skills}
                    onChange={(e) => setNewJob({ ...newJob, skills: e.target.value })}
                    rows={2}
                    className="text-sm"
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setIsCreateModalOpen(false)} className="text-xs sm:text-sm">
                취소
              </Button>
              <Button onClick={() => {
                setIsCreateModalOpen(false);
                setNewJob({ title: '', department: '', purpose: '', responsibilities: '', knowledge: '', skills: '' });
              }} className="text-xs sm:text-sm">
                등록
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardHeader className="p-3 sm:p-6">
          <CardTitle className="text-sm sm:text-base">검색 및 필터</CardTitle>
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="직무명, 부서명 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <SelectTrigger className="w-28 sm:w-40 text-xs sm:text-sm">
                  <SelectValue placeholder="부서" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">전체</SelectItem>
                  {departments.slice(1).map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="hidden sm:flex text-xs sm:text-sm">
                <Filter className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                필터
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Job List Table */}
      <Card>
        <CardHeader className="p-3 sm:p-6">
          <CardTitle className="text-sm sm:text-base">직무 목록</CardTitle>
          <CardDescription className="text-xs sm:text-sm">총 {filteredJobs.length}개 직무</CardDescription>
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs sm:text-sm whitespace-nowrap">직무명</TableHead>
                  <TableHead className="text-xs sm:text-sm whitespace-nowrap hidden sm:table-cell">부서</TableHead>
                  <TableHead className="text-xs sm:text-sm whitespace-nowrap hidden md:table-cell">작성일</TableHead>
                  <TableHead className="text-xs sm:text-sm whitespace-nowrap">상태</TableHead>
                  <TableHead className="text-xs sm:text-sm whitespace-nowrap text-right">액션</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredJobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>
                      <div className="font-medium text-xs sm:text-sm">{job.title}</div>
                      <div className="text-[10px] sm:text-xs text-muted-foreground sm:hidden">{job.department}</div>
                    </TableCell>
                    <TableCell className="text-xs sm:text-sm hidden sm:table-cell">{job.department}</TableCell>
                    <TableCell className="text-xs sm:text-sm hidden md:table-cell">{job.createdDate}</TableCell>
                    <TableCell>
                      <Badge variant={job.status === '완료' ? 'default' : 'secondary'} className="text-[10px] sm:text-xs">
                        {job.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1 sm:gap-2">
                        <Sheet>
                          <SheetTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7 sm:h-8 sm:w-8"
                              onClick={() => setSelectedJob(job)}
                            >
                              <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                          </SheetTrigger>
                          <SheetContent className="w-[95vw] sm:w-[600px] overflow-y-auto">
                            <JobDetailPanel job={selectedJob} />
                          </SheetContent>
                        </Sheet>
                        <Button variant="outline" size="icon" className="h-7 w-7 sm:h-8 sm:w-8">
                          <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function JobDetailPanel({ job }: { job: Job | null }) {
  if (!job) return null;

  return (
    <>
      <SheetHeader>
        <SheetTitle className="text-base sm:text-lg">{job.title}</SheetTitle>
        <SheetDescription className="text-xs sm:text-sm">
          {job.department} · {job.author} · {job.createdDate}
        </SheetDescription>
      </SheetHeader>

      <div className="mt-4 sm:mt-6">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4 h-8 sm:h-10">
            <TabsTrigger value="overview" className="text-[10px] sm:text-xs">개요</TabsTrigger>
            <TabsTrigger value="ksa" className="text-[10px] sm:text-xs">KSA</TabsTrigger>
            <TabsTrigger value="requirements" className="text-[10px] sm:text-xs">요구</TabsTrigger>
            <TabsTrigger value="career" className="text-[10px] sm:text-xs">경력</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            <Card>
              <CardHeader className="p-3 sm:p-4">
                <CardTitle className="text-xs sm:text-sm">직무 목적</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                <p className="text-xs sm:text-sm">{job.purpose}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 sm:p-4">
                <CardTitle className="text-xs sm:text-sm">주요 책무</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                <ul className="list-disc list-inside space-y-1 text-xs sm:text-sm">
                  {job.responsibilities.map((resp, index) => (
                    <li key={index}>{resp}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ksa" className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            <Card>
              <CardHeader className="p-3 sm:p-4">
                <CardTitle className="text-xs sm:text-sm">지식 (Knowledge)</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                <ul className="list-disc list-inside space-y-1 text-xs sm:text-sm">
                  {job.knowledge.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 sm:p-4">
                <CardTitle className="text-xs sm:text-sm">기술 (Skills)</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                <ul className="list-disc list-inside space-y-1 text-xs sm:text-sm">
                  {job.skills.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="requirements" className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            <Card>
              <CardHeader className="p-3 sm:p-4">
                <CardTitle className="text-xs sm:text-sm">필요 자격</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                <ul className="list-disc list-inside space-y-1 text-xs sm:text-sm">
                  {job.qualifications.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="career" className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            <Card>
              <CardHeader className="p-3 sm:p-4">
                <CardTitle className="text-xs sm:text-sm">선행 직무</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                <div className="flex flex-wrap gap-1 sm:gap-2">
                  {job.previousJobs.map((item, index) => (
                    <Badge key={index} variant="outline" className="text-[10px] sm:text-xs">{item}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-3 sm:p-4">
                <CardTitle className="text-xs sm:text-sm">후행 직무</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                <div className="flex flex-wrap gap-1 sm:gap-2">
                  {job.nextJobs.map((item, index) => (
                    <Badge key={index} variant="outline" className="text-[10px] sm:text-xs">{item}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
